import { Button, IconButton } from '@chakra-ui/button';
import { FormControl, FormLabel } from '@chakra-ui/form-control';
import Icon from '@chakra-ui/icon';
import { Input } from '@chakra-ui/input';
import { Flex, Heading, Stack } from '@chakra-ui/layout';
import { Radio, RadioGroup } from '@chakra-ui/radio';
import { InputGroup, InputRightElement } from '@chakra-ui/react';
import { useToast } from '@chakra-ui/toast';
import firebase from 'firebase';
import React, { useState } from 'react';
import { Check, Edit2, Trash } from 'react-feather';
import { Controller, UseFormReturn } from 'react-hook-form';
import {
  AdminCoursesModalForm,
  CourseCategory,
  FirebaseCourse,
} from '../../../../types/types';

interface AdminCoursesModalCategoryProps {
  form: UseFormReturn<AdminCoursesModalForm>;
  categories: CourseCategory[];
}

export const AdminCoursesModalCategory: React.FC<AdminCoursesModalCategoryProps> =
  ({ form, categories }) => {
    const [categoryTitle, setCategoryTitle] = useState('');
    const [categoriesState, setCategoriesState] = useState(categories);
    const toast = useToast();

    const onCreateCategory = async (category: string) => {
      try {
        const ref = await firebase
          .firestore()
          .collection('courseCategories')
          .add({
            title: category,
            created: firebase.firestore.FieldValue.serverTimestamp(),
            lastModified: firebase.firestore.FieldValue.serverTimestamp(),
          });
        form.setValue('courseCategoryId', ref.id);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
        toast({
          title: 'Ein unbekannter Fehler ist aufgetreten.',
          description: 'Kategorie konnte nicht erstellt werden',
          status: 'error',
        });
      }
    };

    const onDeleteCategory = async (uid: string) => {
      try {
        await firebase
          .firestore()
          .collection('courses')
          .get()
          .then((collection) => {
            collection.forEach((course) => {
              const courseData = course.data() as FirebaseCourse;
              if (courseData.courseCategoryId === uid) {
                firebase
                  .firestore()
                  .collection('courses')
                  .doc(course.id)
                  .update({
                    courseCategoryId: '',
                  });
              }
            });
          });
        await firebase
          .firestore()
          .collection('courseCategories')
          .doc(uid)
          .delete();
        form.setValue('courseCategoryId', '');
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
        toast({
          title: 'Ein unbekannter Fehler ist aufgetreten.',
          status: 'error',
        });
      }
    };

    return (
      <section>
        <Heading mb={6} size="sm">
          Kategorie
        </Heading>
        <FormControl mb={3}>
          <Controller
            control={form.control}
            name="courseCategoryId"
            render={({ field }) => (
              <RadioGroup
                onChange={field.onChange}
                onBlur={field.onBlur}
                value={field.value}
                colorScheme="brand"
              >
                <Stack>
                  <Radio value="">keine</Radio>
                  {categoriesState.map((category) => (
                    <Flex key={category.uid}>
                      <Radio value={category.uid} />
                      <InputGroup mx={2}>
                        <Input
                          focusBorderColor="brand.500"
                          value={category.title}
                          onChange={(e) =>
                            setCategoriesState(
                              categoriesState.map((cat) =>
                                cat.uid === category.uid
                                  ? { ...cat, title: e.target.value }
                                  : cat
                              )
                            )
                          }
                        />

                        {categories.find((c) => c.uid === category.uid)
                          ?.title !== category.title && (
                          <InputRightElement>
                            <IconButton
                              colorScheme="green"
                              size="sm"
                              aria-label="edit category"
                              icon={<Icon as={Check} />}
                              onClick={async () => {
                                try {
                                  await firebase
                                    .firestore()
                                    .collection('courseCategories')
                                    .doc(category.uid)
                                    .update({ title: category.title.trim() });
                                } catch (e) {
                                  console.log(e);
                                }
                              }}
                            />
                          </InputRightElement>
                        )}
                      </InputGroup>

                      <IconButton
                        variant="ghost"
                        aria-label="löschen"
                        colorScheme="red"
                        icon={<Icon as={Trash} />}
                        onClick={() => onDeleteCategory(category.uid)}
                      />
                    </Flex>
                  ))}
                </Stack>
              </RadioGroup>
            )}
          />
        </FormControl>
        <FormControl mb={6}>
          <FormLabel>Neue Kategorie erstellen:</FormLabel>
          <Flex>
            <Input
              flex={6}
              defaultValue={categoryTitle}
              focusBorderColor="brand.500"
              onChange={(e) => setCategoryTitle(e.target.value)}
            />
            <Button
              flexShrink={0}
              flex={2}
              ml={3}
              leftIcon={<Icon as={Edit2} />}
              isDisabled={!categoryTitle}
              onClick={() => onCreateCategory(categoryTitle)}
            >
              Kategorie erstellen
            </Button>
          </Flex>
        </FormControl>
      </section>
    );
  };
